import type { LegalContent, Locale } from "./types";

export const privacyByLocale: Record<Locale, LegalContent> = {
  en: {
    title: "Privacy Policy",
    lastUpdatedLabel: "Last updated",
    lastUpdated: "2026-09-22",
    sections: [
      {
        heading: "Overview",
        body: [
          "Moodshot takes a photo, works out the mood of it on your device, and plays a matching Apple Music playlist. It has no server of its own. It collects no personal information, has no account system, and includes no analytics or advertising SDK."
        ]
      },
      {
        heading: "Your photos",
        body: [
          "A photo you take is analysed on your device: scene recognition and a reading of its overall brightness, saturation and colour temperature. The photo is not uploaded to our servers, and the analysis result is not sent anywhere at all. iCloud sync is on by default, and while it is on, a photo you choose to save is copied into your own private iCloud database \u2014 see “Saved moments” below."
        ]
      },
      {
        heading: "Saved moments",
        body: [
          "A photo is written to storage only when you save that moment. It goes into the app's private Application Support directory, along with the mood name and the identifier of the playlist that was playing. Deleting the saved entry, or deleting the app, removes it. It is never uploaded to our servers. iCloud sync is on by default, and while it is on, saved moments are also copied into your own private iCloud database \u2014 a space only your Apple ID can read, so we still cannot see them there either. You can turn sync off and remove that iCloud copy at any time in Settings; your moments on this device are unaffected either way.",
          "Like other app data on your device, it is included in your own device backups (iCloud Backup or a computer backup) if you have those turned on."
        ]
      },
      {
        heading: "Apple Music",
        body: [
          "Playlist search and playback go through Apple's MusicKit. What is sent to Apple is a search term derived from the recognized mood, such as “golden hour”, never your photo and never anything identifying you. Apple's handling of that request is governed by Apple's own privacy policy."
        ]
      },
      {
        heading: "Permissions",
        body: [
          "Camera access is used to take the photo. Media & Apple Music access is used to search and play playlists. Both are requested when first needed, and both can be withdrawn in iOS Settings at any time. Denying the camera leaves the rest of the app, including this policy, reachable."
        ]
      },
      {
        heading: "Required-reason API",
        body: [
          "As declared in the app's privacy manifest, Moodshot uses two required-reason APIs. It reads the system boot time (ProcessInfo.systemUptime, Apple reason code 35F9.1) to measure a short window while skipping tracks, and it reads and writes its own user defaults (Apple reason code CA92.1) to remember when to ask for a review and how many record cards you have exported for free. Both stay on your device and are never used for identification or tracking."
        ]
      },
      {
        heading: "Purchases",
        body: [
          "Moodshot offers one non-consumable in-app purchase, “Deluxe”, which unlocks unlimited saved records, unlimited card exports and alternate app icons. Without it the app keeps up to twelve records and exports three cards. The price shown on the App Store product page applies; payment and refunds are handled by Apple. Playing full tracks requires an Apple Music subscription, which is a relationship between you and Apple; without one the app plays the 30-second previews Apple provides. The Deluxe purchase does not include music and changes nothing about playback."
        ]
      },
      {
        heading: "Children",
        body: [
          "Moodshot is not directed at children and collects no information from anyone, including children."
        ]
      },
      {
        heading: "Changes",
        body: [
          "If this policy changes, the updated version will be posted here with a new date."
        ]
      },
      {
        heading: "Contact",
        body: [
          "axqora@gmail.com"
        ]
      }
    ]
  },
  zh: {
    title: "隐私政策",
    lastUpdatedLabel: "最后更新",
    lastUpdated: "2026-09-22",
    sections: [
      {
        heading: "总览",
        body: [
          "Moodshot 通过随手拍下的照片，在你的本地设备上读懂眼前的光影与场景，并播放相契合的 Apple Music 歌单。它没有自己的服务器，不收集任何个人身份信息，没有账号体系，也没有集成任何第三方统计或广告 SDK。"
        ]
      },
      {
        heading: "你拍的照片",
        body: [
          "照片分析全程在你的设备上完成：识别画面中的场景，并读取整体的明暗、浓淡与色温。照片不会上传到我们的服务器，分析结果也不会被发送到任何地方。iCloud 同步默认开启，开启期间你主动收藏的那张照片会被复制进你自己的 iCloud 私有数据库——详见下文「收藏的瞬间」。"
        ]
      },
      {
        heading: "收藏的瞬间",
        body: [
          "只有在你主动收藏时，照片才会写入本地存储。它保存在 App 私有的 Application Support 目录中，连同记录氛围名与当时那份歌单的标识。删除该条收藏或卸载 App 即可彻底移除，不会上传到我们的服务器。iCloud 同步默认开启，开启期间收藏也会被复制进你自己的 iCloud 私有数据库——只有你的 Apple ID 能读取，我们同样看不到。你可以随时在设置里关闭同步并移除 iCloud 里的这份副本，这台设备上的收藏不受影响。",
          "与设备上其他 App 的数据一样，若你开启了 iCloud 备份或电脑整机备份，它会包含在你自己的备份中。"
        ]
      },
      {
        heading: "Apple Music",
        body: [
          "歌单的检索与播放均通过 Apple 的 MusicKit 接口完成。发送给 Apple 的仅包含基于识别氛围推导出的搜索词（例如「日落 温柔」），绝无你的照片，也无任何可识别个人身份的信息。Apple 如何处理该请求，适用 Apple 自身的隐私政策。"
        ]
      },
      {
        heading: "权限",
        body: [
          "相机权限仅用于现场拍照，媒体与 Apple Music 权限用于检索和播放歌单。两项权限均在首次实际需要时按需申请，且随时可在系统「设置」中收回。即使你拒绝相机权限，App 的其他功能（包括本隐私政策）依然可以正常访问。"
        ]
      },
      {
        heading: "required-reason API",
        body: [
          "与 App 隐私清单（Privacy Manifest）声明一致，Moodshot 用到两项 required-reason API：读取系统启动时间（ProcessInfo.systemUptime，Apple 理由码 35F9.1），用于测量切歌时的一小段防抖时间窗口；以及读写本 App 自己的偏好设置（Apple 理由码 CA92.1），用于记录何时该询问评价、以及免费导出的唱片卡片已经用掉几张。两者都只留在你的设备上，绝不用于身份识别或跨应用追踪。"
        ]
      },
      {
        heading: "付费",
        body: [
          "Moodshot 提供一次性买断的「典藏版」内购，解锁无限收藏、无限唱片卡片导出与备用图标；未购买时可存 12 张唱片、可导出 3 张卡片。价格以 App Store 商品页展示为准，付款与退款完全由 Apple 处理。收听完整曲目需要开通 Apple Music 订阅，该订阅关系归属于你与 Apple；未开通订阅时，App 播放 Apple 官方提供的 30 秒试听片段。典藏版不包含音乐，亦不改变任何播放行为。"
        ]
      },
      {
        heading: "儿童",
        body: [
          "Moodshot 不面向儿童，亦不会向包括儿童在内的任何人收集任何信息。"
        ]
      },
      {
        heading: "变更",
        body: [
          "本政策如有更新，修改后的版本将连同更新日期直接发布于此。"
        ]
      },
      {
        heading: "联系",
        body: [
          "axqora@gmail.com"
        ]
      }
    ]
  },
  "zh-Hant": {
    title: "隱私權政策",
    lastUpdatedLabel: "最後更新",
    lastUpdated: "2026-09-22",
    sections: [
      {
        heading: "總覽",
        body: [
          "Moodshot 透過隨手拍下的照片，在你的本機裝置上讀懂眼前的光影與場景，並播放相契合的 Apple Music 播放列表。它沒有自己的伺服器，不收集任何個人身分資訊，沒有帳號系統，也沒有整合任何第三方統計或廣告 SDK。"
        ]
      },
      {
        heading: "你拍的照片",
        body: [
          "照片分析全程在你的裝置上完成：辨識畫面中的場景，並讀取整體的明暗、濃淡與色溫。照片不會上傳到我們的伺服器，分析結果也不會被傳送到任何地方。iCloud 同步預設開啟，開啟期間你主動收藏的那張照片會被複製進你自己的 iCloud 私有資料庫——詳見下文「收藏的瞬間」。"
        ]
      },
      {
        heading: "收藏的瞬間",
        body: [
          "只有在你主動收藏時，照片才會寫入本機儲存空間。它保存在 App 私有的 Application Support 目錄中，連同記錄氛圍名稱與當時那份播放列表的識別碼。刪除該筆收藏或解除安裝 App 即可徹底清除，不會上傳到我們的伺服器。iCloud 同步預設開啟，開啟期間收藏也會被複製進你自己的 iCloud 私有資料庫——只有你的 Apple ID 能讀取，我們同樣看不到。你可以隨時在設定裡關閉同步並移除 iCloud 裡的這份副本，這台裝置上的收藏不受影響。",
          "與裝置上其他 App 的資料一樣，若你開啟了 iCloud 備份或電腦整機備份，它會包含在你自己的備份中。"
        ]
      },
      {
        heading: "Apple Music",
        body: [
          "播放列表的檢索與播放均透過 Apple 的 MusicKit 介面完成。傳送給 Apple 的僅包含依識別氛圍推導出的搜尋字詞（例如「日落 溫柔」），絕無你的照片，也無任何可識別個人身分的資訊。Apple 如何處理該請求，適用 Apple 自身的隱私權政策。"
        ]
      },
      {
        heading: "權限",
        body: [
          "相機權限僅用於現場拍照，媒體與 Apple Music 權限用於檢索和播放播放列表。兩項權限均在首次實際需要時按需申請，且隨時可在系統「設定」中收回。即使你拒絕相機權限，App 的其他功能（包括本隱私權政策）依然可以正常存取。"
        ]
      },
      {
        heading: "required-reason API",
        body: [
          "與 App 隱私權清單（Privacy Manifest）聲明一致，Moodshot 用到兩項 required-reason API：讀取系統開機時間（ProcessInfo.systemUptime，Apple 理由碼 35F9.1），用於測量切歌時的一小段防抖時間窗口；以及讀寫本 App 自己的偏好設定（Apple 理由碼 CA92.1），用於記錄何時該詢問評價、以及免費匯出的唱片卡片已經用掉幾張。兩者都只留在你的裝置上，絕不用於身分識別或跨應用程式追蹤。"
        ]
      },
      {
        heading: "付費",
        body: [
          "Moodshot 提供一次付清買斷的「典藏版」App 內購買，解鎖無限收藏、無限唱片卡片匯出與備用圖示；未購買時可存 12 張唱片、可匯出 3 張卡片。價格以 App Store 商品頁面顯示為準，付款與退款完全由 Apple 處理。收聽完整曲目需要訂閱 Apple Music，該訂閱關係歸屬於你與 Apple；未訂閱時，App 播放 Apple 官方提供的 30 秒試聽片段。典藏版不包含音樂，亦不改變任何播放行為。"
        ]
      },
      {
        heading: "兒童",
        body: [
          "Moodshot 不以兒童為服務對象，亦不會向包括兒童在內的任何人收集任何資訊。"
        ]
      },
      {
        heading: "變更",
        body: [
          "本政策如有更新，修改後的版本將連同更新日期直接發布於此。"
        ]
      },
      {
        heading: "聯絡方式",
        body: [
          "axqora@gmail.com"
        ]
      }
    ]
  }
};
